import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
import TableCardWithStates from '../components/TableCardWithStates';
import ModalFormFooterActions from '../components/ModalFormFooterActions';
import { PageErrorBanner } from '../components/PageErrorBanner';
import { ModalFormErrorBanner } from '../components/ModalFormErrorBanner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Users } from 'lucide-react';
import Modal from '../components/Modal';
import { distributionListsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

interface DistributionList {
  id: number;
  project_id: number;
  name: string;
  description: string | null;
  recipients_to: string[];
  recipients_cc: string[];
  recipients_bcc: string[];
  used_by_count?: number;
  created_at?: string;
  updated_at?: string;
}

const parseAddresses = (raw: string): string[] =>
  raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export default function DistributionListsPage() {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const [lists, setLists] = useState<DistributionList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<DistributionList | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DistributionList | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [form, setForm] = useState({ name: '', description: '', to: '', cc: '', bcc: '' });

  const fetchLists = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const resp = await distributionListsAPI.list(selectedProjectId);
      if (resp.data?.success) setLists(resp.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch mail recipient lists');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', to: '', cc: '', bcc: '' });
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (list: DistributionList) => {
    setEditing(list);
    setForm({
      name: list.name,
      description: list.description ?? '',
      to: (list.recipients_to ?? []).join(', '),
      cc: (list.recipients_cc ?? []).join(', '),
      bcc: (list.recipients_bcc ?? []).join(', '),
    });
    setFormError(null);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim()) {
      setFormError('Name is required');
      return;
    }
    const to = parseAddresses(form.to);
    if (to.length === 0) {
      setFormError('At least one TO recipient is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        project_id: selectedProjectId,
        name: form.name.trim(),
        description: form.description.trim() || null,
        recipients_to: to,
        recipients_cc: parseAddresses(form.cc),
        recipients_bcc: parseAddresses(form.bcc),
      };
      if (editing) {
        await distributionListsAPI.update(editing.id, payload);
      } else {
        await distributionListsAPI.create(payload);
      }
      setIsModalOpen(false);
      await fetchLists();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await distributionListsAPI.remove(deleteConfirm.id);
      setDeleteConfirm(null);
      await fetchLists();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const externalChips = useMemo(() => {
    return (addr: string[]) => addr.filter((a) => !a.toLowerCase().endsWith('@channelplay.in'));
  }, []);

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-brand" />
            Mail Recipient Lists
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Saved recipient groups used by mailers. Editing a list updates every mailer that references it.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 btn-brand text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm"
        >
          <Plus className="w-4 h-4" /> New List
        </button>
      </header>

      {error && <PageErrorBanner message={error} />}

      <TableCardWithStates loading={loading} isEmpty={lists.length === 0} emptyMessage="No mail recipient lists yet.">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Recipients</th>
                <th className="text-left px-4 py-3 font-medium">External</th>
                <th className="text-left px-4 py-3 font-medium">Used By</th>
                <th className="text-center px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {lists.map((l) => {
                const totalRecipients =
                  (l.recipients_to?.length ?? 0) +
                  (l.recipients_cc?.length ?? 0) +
                  (l.recipients_bcc?.length ?? 0);
                const ext = externalChips([
                  ...(l.recipients_to ?? []),
                  ...(l.recipients_cc ?? []),
                  ...(l.recipients_bcc ?? []),
                ]);
                return (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{l.name}</div>
                      {l.description && <div className="text-xs text-gray-500 mt-0.5">{l.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{totalRecipients}</td>
                    <td className="px-4 py-3">
                      {ext.length > 0 ? (
                        <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 text-xs font-medium px-2 py-1 rounded">
                          {ext.length} external
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{l.used_by_count ?? 0} mailer(s)</td>
                    <td className="px-4 py-3 text-center">
                      <TableActionGroup>
                        <TableActionButton action="edit" label="Edit" onClick={() => openEdit(l)} title="Edit" />
                        <TableActionButton action="delete" label="Delete" onClick={() => setDeleteConfirm(l)} title="Delete" />
                      </TableActionGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
      </TableCardWithStates>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Edit Mail Recipient List' : 'New Mail Recipient List'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Project A — Daily Client Recipients"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              TO recipients <span className="text-gray-400">(comma, semicolon, space or newline separated)</span>
            </label>
            <textarea
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
              rows={3}
              placeholder="client@example.com, manager@example.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CC</label>
              <textarea
                value={form.cc}
                onChange={(e) => setForm({ ...form, cc: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">BCC</label>
              <textarea
                value={form.bcc}
                onChange={(e) => setForm({ ...form, bcc: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                rows={2}
              />
            </div>
          </div>
          {formError && <ModalFormErrorBanner message={formError} />}
          <div className="flex justify-end gap-2 pt-2">
            <ModalFormFooterActions
              onCancel={() => setIsModalOpen(false)}
              submitting={submitting}
              editing={editing}
              createLabel="Create List"
            />
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete mail recipient list?"
        size="sm"
      >
        <p className="text-sm text-gray-700">
          This will permanently remove <strong>{deleteConfirm?.name}</strong>. Mailers that reference it must be updated to a different list or removed first.
        </p>
        <div className="flex justify-end gap-2 pt-4">
          <button
            onClick={() => setDeleteConfirm(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
